/**
 * Cobertura horizontal: qué porcentaje del recinto queda dentro del radio de
 * alcance de al menos un DEA.
 *
 * El cálculo es un muestreo por grilla sobre la caja del perímetro: se descartan
 * las celdas que caen fuera del polígono y se cuenta cuántas de las restantes
 * están a menos de `radiusMeters` de algún DEA. Es una aproximación, no una
 * integral — con la grilla por defecto el error es del orden del 1%.
 */

import { boundingBox, distanceMeters, isPointInPolygon } from './geo.js';

export const DEFAULT_GRID_SIZE = 64;

/**
 * @param {[number, number][]} perimeter  Vértices del recinto.
 * @param {[number, number][]} deaCenters Ubicación de cada DEA.
 * @param {number} radiusMeters           Radio de cobertura por DEA.
 * @returns {number|null} Porcentaje entero 0-100, o `null` si no es calculable
 *   (sin perímetro cerrado o sin ningún DEA).
 */
export function coveragePercent(perimeter, deaCenters, radiusMeters, gridSize = DEFAULT_GRID_SIZE) {
  if (!perimeter || perimeter.length < 3) return null;
  if (!deaCenters || deaCenters.length === 0) return null;

  const { minLat, minLng, maxLat, maxLng } = boundingBox(perimeter);
  let inside = 0;
  let covered = 0;

  for (let i = 0; i < gridSize; i++) {
    for (let j = 0; j < gridSize; j++) {
      const lat = minLat + ((maxLat - minLat) * (i + 0.5)) / gridSize;
      const lng = minLng + ((maxLng - minLng) * (j + 0.5)) / gridSize;
      if (!isPointInPolygon(lat, lng, perimeter)) continue;
      inside++;
      for (const [cLat, cLng] of deaCenters) {
        if (distanceMeters(lat, lng, cLat, cLng) <= radiusMeters) {
          covered++;
          break;
        }
      }
    }
  }

  return inside ? Math.round((covered / inside) * 100) : null;
}
